import { useGetList } from 'react-admin';
import { Box, Typography, Paper, Grid, Card, CardContent, Chip, LinearProgress } from '@mui/material';
import { TrendingUp, TrendingDown, AccountBalance, Receipt, Warning, CheckCircle } from '@mui/icons-material';
import { BarChart, LineChart } from '@mui/x-charts-pro';
import { ListPageHeader } from '@/components/ListPageHeader';
import { TrialBalanceCard } from './TrialBalanceCard';
import type { AccountResponse, JournalEntryResponse, InvoiceResponse, ReceiptResponse } from '@rosta/data-model/application-agnostic/ledger/dtos';

const formatCurrency = (amount: number | undefined, currency: string = 'ZAR'): string => {
  if (amount === undefined) return '—';
  return `${currency} ${amount.toLocaleString()}`;
};

const formatCompact = (amount: number | undefined): string => {
  if (amount === undefined) return '—';
  if (Math.abs(amount) >= 1_000_000) return `R ${(amount / 1_000_000).toFixed(1)}M`;
  if (Math.abs(amount) >= 1_000) return `R ${(amount / 1_000).toFixed(0)}K`;
  return `R ${amount.toLocaleString()}`;
};

export const LedgerDashboard = () => {
  const { data: accountsRaw, isLoading: accountsLoading } = useGetList('accounts', { pagination: { page: 1, perPage: 1000 } });
  const { data: journalEntriesRaw, isLoading: entriesLoading } = useGetList('journal-entries', { pagination: { page: 1, perPage: 100 } });
  const { data: invoicesRaw, isLoading: invoicesLoading } = useGetList('invoices', { pagination: { page: 1, perPage: 1000 } });
  const { data: receiptsRaw, isLoading: receiptsLoading } = useGetList('receipts', { pagination: { page: 1, perPage: 100 } });

  const accounts = accountsRaw as unknown as AccountResponse[] | undefined;
  const journalEntries = journalEntriesRaw as unknown as JournalEntryResponse[] | undefined;
  const invoices = invoicesRaw as unknown as InvoiceResponse[] | undefined;
  const receipts = receiptsRaw as unknown as ReceiptResponse[] | undefined;

  const isLoading = accountsLoading || entriesLoading || invoicesLoading || receiptsLoading;

  // Account metrics
  const accountCount = accounts?.length ?? 0;
  const activeAccountCount = accounts?.filter((a) => a.status === 'Active').length ?? 0;
  const accountsByType = accounts?.reduce((acc, a) => {
    acc[a.chartType] = (acc[a.chartType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) ?? {};

  // Journal entry metrics
  const entryCount = journalEntries?.length ?? 0;
  const postedEntries = journalEntries?.filter((e) => e.status === 'Posted').length ?? 0;
  const draftEntries = journalEntries?.filter((e) => e.status === 'Draft').length ?? 0;
  const totalDebit = journalEntries?.filter((e) => e.status === 'Posted').reduce((sum, e) => sum + (e.totalDebit ?? 0), 0) ?? 0;
  const totalCredit = journalEntries?.filter((e) => e.status === 'Posted').reduce((sum, e) => sum + (e.totalCredit ?? 0), 0) ?? 0;

  // Invoice metrics
  const invoiceCount = invoices?.length ?? 0;
  const paidInvoices = invoices?.filter((i) => i.status === 'Paid').length ?? 0;
  const overdueInvoices = invoices?.filter((i) => i.status === 'Overdue').length ?? 0;
  const totalInvoiceValue = invoices?.reduce((sum, i) => sum + (i.total ?? 0), 0) ?? 0;
  const outstandingInvoiceValue = invoices?.filter((i) => i.status !== 'Paid' && i.status !== 'Cancelled').reduce((sum, i) => sum + (i.total ?? 0), 0) ?? 0;

  // Receipt metrics
  const receiptCount = receipts?.length ?? 0;
  const allocatedReceipts = receipts?.filter((r) => r.status === 'Allocated').length ?? 0;
  const totalReceiptValue = receipts?.reduce((sum, r) => sum + (r.amount ?? 0), 0) ?? 0;

  // Chart data
  const chartTypeData = Object.entries(accountsByType).map(([label, value]) => ({ label, value }));

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading ledger data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>Ledger Dashboard</Typography>

      <ListPageHeader
        title="Financial Overview"
        metrics={[
          { label: 'Total Accounts', value: String(accountCount), color: 'primary.main' },
          { label: 'Posted Entries', value: String(postedEntries), color: 'success.main' },
          { label: 'Outstanding Invoices', value: formatCompact(outstandingInvoiceValue), color: 'error.main' },
          { label: 'Total Receipts', value: formatCompact(totalReceiptValue), color: 'info.main' },
        ]}
        pieChartData={chartTypeData}
        pieChartTitle="Accounts by Type"
      />

      <TrialBalanceCard />

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Accounts by Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Chart of Accounts</Typography>
              </Box>
              <Grid container spacing={2}>
                {Object.entries(accountsByType).map(([type, count]) => (
                  <Grid size={{ xs: 6 }} key={type}>
                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">{type}</Typography>
                      <Typography variant="h4" color="primary.main">{count}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {activeAccountCount} of {accountCount} accounts active
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Journal Entries */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ mr: 1, color: 'success.main' }} />
                <Typography variant="h6">Journal Entries</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="caption">Posted</Typography>
                    <Typography variant="h4" color="success.dark">{postedEntries}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
                    <Typography variant="caption">Draft</Typography>
                    <Typography variant="h4" color="info.dark">{draftEntries}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Debit</Typography>
                    <Typography variant="h6" color="error.main">{formatCompact(totalDebit)}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Credit</Typography>
                    <Typography variant="h6" color="success.main">{formatCompact(totalCredit)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
              {totalDebit !== totalCredit && (
                <Chip
                  label="Double-entry violation: Debit ≠ Credit"
                  color="error"
                  icon={<Warning />}
                  sx={{ mt: 2 }}
                />
              )}
              {totalDebit === totalCredit && totalDebit > 0 && (
                <Chip
                  label="Double-entry balanced"
                  color="success"
                  icon={<CheckCircle />}
                  sx={{ mt: 2 }}
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Invoices */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Receipt sx={{ mr: 1, color: 'info.main' }} />
                <Typography variant="h6">Invoices</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="h5">{invoiceCount}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="caption">Paid</Typography>
                    <Typography variant="h5" color="success.dark">{paidInvoices}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
                    <Typography variant="caption">Overdue</Typography>
                    <Typography variant="h5" color="error.dark">{overdueInvoices}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Value</Typography>
                    <Typography variant="h6" color="primary.main">{formatCompact(totalInvoiceValue)}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                    <Typography variant="h6" color="error.main">{formatCompact(outstandingInvoiceValue)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Receipts */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingDown sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Receipts</Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total</Typography>
                    <Typography variant="h5">{receiptCount}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
                    <Typography variant="caption">Allocated</Typography>
                    <Typography variant="h5" color="success.dark">{allocatedReceipts}</Typography>
                  </Paper>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Total Value</Typography>
                    <Typography variant="h6" color="success.main">{formatCompact(totalReceiptValue)}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
